import {
  Pagination as PaginationNav,
  PaginationEllipsis,
  PaginationNext,
  PaginationPage,
  PaginationPrevious,
} from '@vs-shop/commerce-ui/pagination';
import { generatePaginationRange } from '@vs-shop/domain/pagination';

import { paginationSmallScreenPagesCount } from '../../config/pagination';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../utils/intl';

import type { ChangeNumberAtomPromise } from '@vs-shop/engine/atom/products/pagination';
import type { HydraViewExtended } from '@vs-shop/schemas/openapi/hydraView';
import { getFullUrl } from '../../effect/utils/url/canonical';
import type * as Atom from 'effect/unstable/reactivity/Atom';
import { useAtomValue } from '@effect/atom-react';

type Props = {
  hydraViewExtended: HydraViewExtended;
  changePage: ChangeNumberAtomPromise;
  scrollToGrid: () => void;
  baseUrl: string;
  lastPageLoadedIncludingLoadMoreAtom: Atom.Atom<number>;
  searchParams?: Record<string, string> | undefined;
  pageParamName?: string;
};

/**
 * rcprofi's pagination: the shared `@vs-shop/commerce-ui` parts fed this
 * tenant's paging.
 *
 * Everything the shared parts may not carry is resolved here — the atom that
 * knows how far load-more has run, the response's own page, the range between
 * the two, the URLs each page lives at, and the words. What crosses into the
 * parts is numbers, hrefs and children.
 */
export default function Pagination({
  hydraViewExtended,
  changePage,
  scrollToGrid,
  baseUrl,
  lastPageLoadedIncludingLoadMoreAtom,
  searchParams = {},
  pageParamName,
}: Props) {
  const hydraViewNumbers = hydraViewExtended.hydraViewNumbers;
  const currentPageFromResponse = hydraViewNumbers.current;
  const lastPageLoadedIncludingLoadMore = useAtomValue(lastPageLoadedIncludingLoadMoreAtom);

  // Load-more appends pages to the grid without moving the response's own
  // page, so the page a shopper is reading is the furthest one rendered. Which
  // of the two counts is a statement about how THIS tenant's load-more
  // interacts with paging, not about how a pagination control looks.
  const isLoadMoreUsed = currentPageFromResponse < lastPageLoadedIncludingLoadMore;
  const currentPage = isLoadMoreUsed ? lastPageLoadedIncludingLoadMore : currentPageFromResponse;

  const paginationItems = generatePaginationRange(currentPage, hydraViewNumbers.last);

  const intl = getMemoizedIntlShapeWithDefaultLocale();

  const onPageClick = async (page: number) => {
    await changePage(page);
    scrollToGrid();
  };

  const hrefFor = (page: number) => getFullUrl(baseUrl, page, searchParams, pageParamName);

  return (
    <div className="flex w-full justify-center py-3">
      <PaginationNav
        aria-label={intl.formatMessage({
          id: 'container.pagination.pagination',
          defaultMessage: 'Paginace',
        })}
      >
        {currentPage !== hydraViewNumbers.first && (
          <PaginationPrevious
            href={hrefFor(currentPage - 1)}
            onClick={async (e) => {
              e.preventDefault();
              await onPageClick(currentPage - 1);
            }}
            aria-label={intl.formatMessage({
              id: 'container.pagination.previousPage',
              defaultMessage: 'Předchozí stránka',
            })}
          />
        )}

        {paginationItems.map((item, index) => {
          if (item.type === 'ellipsis') {
            return <PaginationEllipsis key={`ellipsis-${String(index)}`}>...</PaginationEllipsis>;
          }

          const pageNumber = item.page;

          if (pageNumber === undefined) {
            return null;
          }

          const intlPageNumber = intl.formatNumber(pageNumber, {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });

          return (
            <PaginationPage
              key={pageNumber}
              page={pageNumber}
              lastPage={hydraViewNumbers.last}
              smallScreenPagesCount={paginationSmallScreenPagesCount}
              href={hrefFor(pageNumber)}
              onClick={async (e) => {
                e.preventDefault();
                await onPageClick(pageNumber);
              }}
              aria-current={pageNumber === currentPage ? 'page' : undefined}
              aria-label={intl.formatMessage(
                {
                  id: 'container.pagination.pageNumber',
                  defaultMessage: 'Stránka {pageNumber}',
                },
                { pageNumber: intlPageNumber }
              )}
            >
              {pageNumber}
            </PaginationPage>
          );
        })}

        {currentPage !== hydraViewNumbers.last && (
          <PaginationNext
            href={hrefFor(currentPage + 1)}
            onClick={async (e) => {
              e.preventDefault();
              await onPageClick(currentPage + 1);
            }}
            aria-label={intl.formatMessage({
              id: 'container.pagination.nextPage',
              defaultMessage: 'Následující stránka',
            })}
          />
        )}
      </PaginationNav>
    </div>
  );
}
